'use client';

import type { ViewableDocument } from '@govinity/ui/components/ui-custom/documents-viewer';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@govinity/ui/components/ui/resizable';
import { useTranslations } from 'next-intl';
import { type ReactNode, useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import { usePersistedLayout } from '@/app/members/meetings/[id]/hooks/use-persisted-layout';
import { MeetingDocumentViewer } from './meeting-document-viewer';
import type { TranslatableDocument } from './translatable-documents';

const NAV_PANEL_ID = 'reader-nav';
const DOCUMENT_PANEL_ID = 'reader-document';
const DEFAULT_READER_LAYOUT = { [NAV_PANEL_ID]: 32, [DOCUMENT_PANEL_ID]: 68 };

/** Tailwind's `lg` breakpoint — below it a side-by-side reading layout has no room. */
const DESKTOP_QUERY = '(min-width: 1024px)';

const subscribeToDesktopQuery = (onStoreChange: () => void) => {
  const media = window.matchMedia(DESKTOP_QUERY);
  media.addEventListener('change', onStoreChange);

  return () => media.removeEventListener('change', onStoreChange);
};

type DocumentReaderProps = {
  autoRequestTranslation?: boolean;
  document: ViewableDocument & TranslatableDocument;
  /** Identifies the open document, so the reader re-scrolls to its top when a different one opens. */
  documentKey: number | string;
  /** localStorage key the dragged split is remembered under, one per tab that hosts a reader. */
  layoutStorageKey: string;
  nav: ReactNode;
  onClose: () => void;
  searchText?: string;
  title: string;
};

/**
 * Reading mode for a meeting tab: the tab's own content collapses into a document index on the left
 * and the open document fills the right, at a split the reader can drag and which is remembered for
 * their next visit. Anything narrower than a laptop has no room for both, so it gets the document
 * alone — closing it is the way back to the tab.
 */
export function DocumentReader({
  autoRequestTranslation,
  document,
  documentKey,
  layoutStorageKey,
  nav,
  onClose,
  searchText,
  title,
}: DocumentReaderProps) {
  const t = useTranslations('DocumentReader');

  /**
   * Whether the viewport is at least Tailwind's `lg` width, tracked in JavaScript rather than with a
   * `hidden lg:block` class: a resizable panel hidden with CSS still holds its share of the row, so
   * the navigation panel has to be left out of the tree entirely on narrower screens. Renders as
   * narrow on the server, then corrects during hydration.
   */
  const isDesktop = useSyncExternalStore(
    subscribeToDesktopQuery,
    () => window.matchMedia(DESKTOP_QUERY).matches,
    () => false
  );

  const readerRef = useRef<HTMLDivElement>(null);
  const { layout, handleLayoutChanged } = usePersistedLayout(
    layoutStorageKey,
    DEFAULT_READER_LAYOUT
  );

  // Brings the reader's own top edge — the heading of its document index — to the top of the page
  // each time it opens a document. A reader is entered by clicking a row anywhere down a long list,
  // so without this it would appear already scrolled past its own heading.
  useLayoutEffect(() => {
    readerRef.current?.scrollIntoView();
  }, [documentKey]);

  return (
    <div className="bg-background" ref={readerRef}>
      <ResizablePanelGroup
        className="border"
        defaultLayout={layout}
        onLayoutChanged={handleLayoutChanged}
      >
        {isDesktop && (
          <ResizablePanel
            defaultSize={32}
            id={NAV_PANEL_ID}
            minSize={20}
            // A panel is a scrollport of its own, which would trap the sticky navigation inside
            // this column. Turning that off lets the navigation stick to the page scroll and do
            // its own scrolling within the height it is capped at.
            style={{ maxHeight: 'none', overflow: 'visible' }}
          >
            {nav}
          </ResizablePanel>
        )}
        {isDesktop && <ResizableHandle withHandle />}
        <ResizablePanel
          className="min-w-0 p-2"
          defaultSize={68}
          id={DOCUMENT_PANEL_ID}
          minSize={40}
        >
          <MeetingDocumentViewer
            autoRequestTranslation={autoRequestTranslation}
            closeLabel={t('closeLabel')}
            compact
            document={document}
            onClose={onClose}
            searchText={searchText}
            title={title}
          />
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}
